import flatpickr from 'flatpickr'
import { German } from 'flatpickr/dist/l10n/de.js'

export default class FlatpickrInput extends HTMLElement {
  connectedCallback() {
    this._init()

    this._beforeMorphHandler = (event) => {
      if (this === event.target || this.contains(event.target)) {
        this._destroy()
      }
    }
    this._reinitHandler = () => {
      if (!this.fp) this._init()
    }

    document.addEventListener(
      'turbo:before-morph-element',
      this._beforeMorphHandler
    )
    document.addEventListener('turbo:morph', this._reinitHandler)
    document.addEventListener('turbo:frame-render', this._reinitHandler)
  }

  disconnectedCallback() {
    document.removeEventListener(
      'turbo:before-morph-element',
      this._beforeMorphHandler
    )
    document.removeEventListener('turbo:morph', this._reinitHandler)
    document.removeEventListener('turbo:frame-render', this._reinitHandler)
    this._destroy()
  }

  _init() {
    if (this.fp) return

    const dateInput = this.querySelector('input[type="date"]')
    const datetimeInput = this.querySelector('input[type="datetime-local"]')
    const input = dateInput || datetimeInput
    if (!input) return

    const isDatetime = input.type === 'datetime-local'

    this.fp = flatpickr(input, {
      locale: German,
      dateFormat: isDatetime ? 'Y-m-dTH:i' : 'Y-m-d',
      altInput: true,
      altFormat: isDatetime ? 'd.m.Y H:i' : 'd.m.Y',
      ariaDateFormat: isDatetime ? 'd. MMMM Y H:i' : 'd. MMMM Y',
      enableTime: isDatetime,
      time_24hr: isDatetime,
      allowInput: true,
      onReady: (selectedDates, dateStr, fp) => this._onReady(fp)
    })
  }

  _onReady(fp) {
    const visibleInput = fp.altInput || fp.input
    const originalInput = fp.input

    // Find the label for the original input
    let label =
      originalInput.labels?.[0] ||
      document.querySelector(`label[for="${originalInput.id}"]`)
    if (label && !label.id) {
      label.id = `${originalInput.id || 'fp'}-label`
    }
    if (label) {
      visibleInput.setAttribute('aria-labelledby', label.id)
      // Make label click focus and open the visible (alt) input
      label.addEventListener('click', (e) => {
        e.preventDefault()
        visibleInput.focus()
        fp.open()
      })
    }

    // Link visible input to the calendar popup for screen readers
    const cal = fp.calendarContainer
    if (cal) {
      if (!cal.id) cal.id = `${originalInput.id || 'fp'}-calendar`
      visibleInput.setAttribute('aria-controls', cal.id)
      visibleInput.setAttribute('aria-haspopup', 'dialog')
    }

    // Propagate required/disabled state to the visible input
    if (originalInput.required)
      visibleInput.setAttribute('aria-required', 'true')
    if (originalInput.disabled)
      visibleInput.setAttribute('aria-disabled', 'true')

    // Hide original input from AT since altInput is the visible one
    if (fp.altInput) {
      originalInput.setAttribute('aria-hidden', 'true')
      originalInput.setAttribute('tabindex', '-1')
    }

    this._addClearButton(fp)
  }

  _addClearButton(fp) {
    const visibleInput = fp.altInput || fp.input

    const inputGroup = document.createElement('div')
    inputGroup.className = 'input-group'
    visibleInput.parentNode.insertBefore(inputGroup, visibleInput)
    inputGroup.appendChild(visibleInput)

    const clearBtn = document.createElement('button')
    clearBtn.type = 'button'
    clearBtn.className = 'btn btn-secondary flatpickr-clear'
    clearBtn.setAttribute('aria-label', 'Datum löschen')
    clearBtn.innerHTML = '&times;'
    clearBtn.hidden = fp.selectedDates.length === 0

    clearBtn.addEventListener('click', (e) => {
      e.preventDefault()
      fp.clear()
    })

    inputGroup.appendChild(clearBtn)

    fp.config.onChange.push(() => {
      clearBtn.hidden = fp.selectedDates.length === 0
    })
  }

  _destroy() {
    try {
      this.fp?.destroy()
    } catch (_e) {
      // flatpickr DOM may already be gone after morph
    }
    this.fp = null
  }
}
